import React, { useState } from 'react';
import { Box, Typography, IconButton, Grid, useMediaQuery, useTheme } from '@mui/material';
import { ChevronLeft, ChevronRight, AccessTime, LocationOn } from '@mui/icons-material';
import moment from 'moment';
import { COLORS } from '../constants/colors';

// Set up moment locale
moment.locale('zh-tw');

interface HistoryEvent {
  eventId: string;
  eventTitle: string;
  eventDate: string;
  eventTime: string;
  eventLocation: string;
  spots: number;
  fee: number;
  checkedIn: boolean;
  paid: boolean;
  approved: boolean;
  joinedAt: string;
  sendDetails?: string;
  otherDetails?: string;
  totalParticipants?: number;
}

interface EventCalendarProps {
  events: HistoryEvent[];
  onEventClick?: (event: HistoryEvent) => void;
}

const EventCalendar: React.FC<EventCalendarProps> = ({ events, onEventClick }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [currentWeek, setCurrentWeek] = useState(moment());
  const [mobileOffset, setMobileOffset] = useState(0); // Track mobile date offset
  
  // Drag scrolling state
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [dragDistance, setDragDistance] = useState(0);

  // Create array of days based on screen size
  const days = [];
  if (isMobile) {
    // Mobile: show 3 days starting from offset
    for (let i = 0; i < 3; i++) {
      days.push(moment().add(mobileOffset + i, 'days'));
    }
  } else {
    // Desktop: show full week
    const weekStart = currentWeek.clone().startOf('week');
    for (let i = 0; i < 7; i++) {
      days.push(weekStart.clone().add(i, 'days'));
    }
  }

  // const weekStart = isMobile ? days[0] : currentWeek.clone().startOf('week');
  // const weekEnd = isMobile ? days[days.length - 1] : currentWeek.clone().endOf('week');

  // Group events by date
  const eventsByDate = events.reduce((acc, event) => {
    const eventDate = moment(event.eventDate);
    const dateKey = eventDate.format('YYYY-MM-DD');
    if (!acc[dateKey]) {
      acc[dateKey] = [];
    }
    acc[dateKey].push(event);
    return acc;
  }, {} as Record<string, HistoryEvent[]>);


  const handleEventClick = (event: HistoryEvent) => {
    if (onEventClick) {
      onEventClick(event);
    }
  };

  const goToPreviousWeek = () => {
    setCurrentWeek(currentWeek.clone().subtract(1, 'week'));
  };

  const goToNextWeek = () => {
    setCurrentWeek(currentWeek.clone().add(1, 'week'));
  };

  const goToToday = () => {
    setCurrentWeek(moment());
    setMobileOffset(0); // Reset mobile offset to today
  };

  // Mobile navigation functions
  const goToPrevious3Days = () => {
    setMobileOffset(mobileOffset - 3);
  };

  const goToNext3Days = () => {
    setMobileOffset(mobileOffset + 3);
  };

  // Drag scrolling handlers
  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    setIsDragging(true);
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    setStartX(clientX);
    setDragDistance(0);
  };

  const handleDragMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDragging) return;
    
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const distance = clientX - startX;
    setDragDistance(distance);
  };

  const handleDragEnd = () => {
    if (!isDragging) return;
    
    setIsDragging(false);
    
    // Threshold for switching weeks (50px)
    const threshold = 50;
    
    if (Math.abs(dragDistance) > threshold) {
      if (dragDistance > 0) {
        // Dragged right - go to previous week/days
        if (isMobile) {
          goToPrevious3Days();
        } else {
          goToPreviousWeek();
        }
      } else {
        // Dragged left - go to next week/days
        if (isMobile) {
          goToNext3Days();
        } else {
          goToNextWeek();
        }
      }
    }
    
    setDragDistance(0);
  };

  // if (events.length === 0) {
  //   return (
  //     <Paper elevation={1} sx={{ height: '400px', p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
  //       <Typography variant="h6" color="text.secondary">
  //         沒有活動資料
  //       </Typography>
  //     </Paper>
  //   );
  // }

  return (
    <Box sx={{ p: 0 }}>
      {/* Header with navigation */}
      {!isMobile ? (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={goToPreviousWeek} size="small">
            <ChevronLeft />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 600, textAlign: 'center' }}>
            {moment().format('MM月')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton onClick={goToToday} size="small" sx={{ fontSize: '0.8rem' }}>
              今天
            </IconButton>
            <IconButton onClick={goToNextWeek} size="small">
              <ChevronRight />
            </IconButton>
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={goToPrevious3Days} size="small">
            <ChevronLeft />
          </IconButton>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, textAlign: 'center' }}>
            {days[0].format('MM/DD')} - {days[2].format('MM/DD')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton onClick={goToToday} size="small" sx={{ fontSize: '0.7rem', px: 1 }}>
              今天
            </IconButton>
            <IconButton onClick={goToNext3Days} size="small">
              <ChevronRight />
            </IconButton>
          </Box>
        </Box>
      )}
      {/* Week grid */}
      <Grid 
        container 
        spacing={1}
        onMouseDown={handleDragStart}
        onMouseMove={handleDragMove}
        onMouseUp={handleDragEnd}
        onMouseLeave={handleDragEnd}
        onTouchStart={handleDragStart}
        onTouchMove={handleDragMove}
        onTouchEnd={handleDragEnd}
        sx={{
          cursor: isDragging ? 'grabbing' : 'grab',
          userSelect: 'none',
          transform: `translateX(${dragDistance}px)`,
          transition: isDragging ? 'none' : 'transform 0.3s ease-out'
        }}
      >
        {days.map((day, index) => {
          const dateKey = day.format('YYYY-MM-DD');
          const dayEvents = eventsByDate[dateKey] || [];
          const isToday = day.isSame(moment(), 'day');
          const isPast = day.isBefore(moment(), 'day');
          const isSunday = day.day() === 0;
          const isSaturday = day.day() === 6;
          const dateColor = isToday ? '#1976d2' : isSunday ? COLORS.DATE.SUNDAY : isSaturday ? COLORS.DATE.SATURDAY : COLORS.DATE.DEFAULT;

          return (
            <Grid item xs={isMobile ? 4 : true} key={index}>
              <Box
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  p: 1,
                  height: isMobile ? '120px' : '150px',
                  backgroundColor: isToday ? '#e3f2fd' : isPast ? '#f5f5f5' : 'white',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {/* Day header */}
                <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'center', alignItems: 'center', mb: 1, flexShrink: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: isToday ? 700 : 500, color: dateColor, textAlign: 'center' }}>
                    {isMobile ? `${day.format('DD')} ${day.format('ddd').replace('Mon', '一').replace('Tue', '二').replace('Wed', '三').replace('Thu', '四').replace('Fri', '五').replace('Sat', '六').replace('Sun', '日')}` : day.format('DD')}
                  </Typography>
                  {!isMobile && (
                    <Typography variant="caption" sx={{ ml: 'auto', color: isSunday ? COLORS.DATE.SUNDAY : isSaturday ? COLORS.DATE.SATURDAY : 'text.secondary' }}>
                      {day.format('ddd').replace('Mon', '一').replace('Tue', '二').replace('Wed', '三').replace('Thu', '四').replace('Fri', '五').replace('Sat', '六').replace('Sun', '日')}
                    </Typography>
                  )}
                </Box>

                {/* Events for this day */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5, overflowY: 'auto', overflowX: 'hidden' }}>
                  {dayEvents.map((event, eventIndex) => (
                    <Box
                      key={`${event.eventId}-${eventIndex}`}
                      onClick={() => handleEventClick(event)}
                      sx={{
                        p: 0.5,
                        backgroundColor: !event.approved ? '#fff3e0' : !event.paid ? '#ffebee' : !event.checkedIn ? '#e8f5e8' : '#e3f2fd',
                        border: '1px solid',
                        borderColor: !event.approved ? '#ff9800' : !event.paid ? '#f44336' : !event.checkedIn ? '#4caf50' : '#2196f3',
                        borderRadius: 0.5,
                        cursor: 'pointer',
                        '&:hover': {
                          opacity: 0.8,
                        },
                      }}
                    >
                      {!isMobile && (
                        <>
                          <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem', lineHeight: 1.2, display: 'block', mb: 0.25 }}>
                            {event.eventTitle}
                          </Typography>
                          {event.sendDetails && (
                            <Typography variant="caption" sx={{ fontSize: '0.65rem', lineHeight: 1.2, display: 'block', mb: 0.25, color: 'text.secondary' }}>
                              {event.sendDetails}
                            </Typography>
                          )}
                        </>
                      )}
                      {isMobile && (
                        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem', lineHeight: 1.2, display: 'block', mb: 0.25 }}>
                          {event.sendDetails}
                        </Typography>
                      )}
                      {(() => {
                        // Parse time range (e.g., "10:00-12:00" or "10:00 - 12:00")
                        const timeMatch = event.eventTime.match(/(\d{1,2}:\d{2})\s*[-~]\s*(\d{1,2}:\d{2})/);
                        if (timeMatch) {
                          const [, startTime, endTime] = timeMatch;
                          return (
                            <>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                <AccessTime sx={{ fontSize: isMobile ? '0.7rem' : '0.65rem', color: 'text.secondary' }} />
                                <Typography variant="caption" sx={{ fontSize: isMobile ? '0.65rem' : '0.6rem', color: 'text.secondary', fontWeight: isMobile ? 600 : 400, lineHeight: 1.3 }}>
                                  {startTime}-{endTime}
                                </Typography>
                              </Box>
                            </>
                          );
                        }
                        // Fallback: show original time if format doesn't match
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, mb: 0.25 }}>
                            <AccessTime sx={{ fontSize: isMobile ? '0.7rem' : '0.65rem', color: 'text.secondary' }} />
                            <Typography variant="caption" sx={{ fontSize: isMobile ? '0.65rem' : '0.6rem', color: 'text.secondary', fontWeight: isMobile ? 600 : 400 }}>
                              {event.eventTime}
                            </Typography>
                          </Box>
                        );
                      })()}
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.3 }}>
                        <LocationOn sx={{ fontSize: isMobile ? '0.7rem' : '0.65rem', color: 'text.secondary', flexShrink: 0, mt: 0.1 }} />
                        <Typography variant="caption" sx={{ fontSize: isMobile ? '0.65rem' : '0.6rem', color: 'text.secondary', lineHeight: 1.3, wordBreak: 'break-word' }}>
                          {event.eventLocation}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default EventCalendar;